import { useState, type CSSProperties } from "react";
import { ApiError, type FeedbackPriority } from "@keydials/api-client";
import { api, getAccessToken } from "../../auth/AuthContext";
import { hexA } from "../../lib/ui";
import { Icon } from "../Icon";
import type { FeedbackDraft } from "./feedbackDraft";
import { AttachmentTray, useFeedbackAttachments } from "./FeedbackAttachments";

const feedbackFieldStyle: CSSProperties = {
  width: "100%",
  resize: "vertical",
  font: "inherit",
  fontSize: 14.5,
  padding: "12px 14px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  outline: "none",
};

const feedbackLabelStyle: CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text)",
  margin: "0 0 6px",
};

const PRIORITIES: { value: FeedbackPriority; label: string; hint: string }[] = [
  { value: "Urgent", label: "Urgent", hint: "blocking" },
  { value: "Normal", label: "Normal", hint: "can work around" },
  { value: "Low", label: "Low", hint: "nice-to-have" },
];

// web-user has no shared secondary-button class (only .kd-btn-primary and .kd-btn-dashed), so Cancel/
// Close are styled inline to match the weight of the priority/tab toggle buttons above.
const secondaryBtnStyle: CSSProperties = {
  height: 42,
  padding: "0 18px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text-muted)",
  font: "inherit",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};

/**
 * The feedback form itself, with no opinion about what it sits inside.
 *
 * Split out of the old `FeedbackModal` so the same fields can live in the floating dock (KEY-445,
 * mirroring web-admin's KEY-327). The values come from the CALLER — the dock keeps them above the
 * router so navigating away doesn't unmount them, which is the whole point of the change. Attachments
 * stay local: they are `File` objects, so they can't be persisted anyway (see the note below).
 */
export function FeedbackForm({
  draft,
  onChange,
  onSent,
  onCancel,
  cancelLabel = "Cancel",
}: {
  draft: FeedbackDraft;
  onChange: (patch: Partial<FeedbackDraft>) => void;
  /** Called after a successful send, so the host can clear the draft and tell the user. */
  onSent: () => void;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const attachments = useFeedbackAttachments();

  const isBug = draft.tab === "Bug";
  const canSubmit = isBug
    ? draft.whatWereYouDoing.trim() !== "" && draft.whatDidYouExpect.trim() !== "" && draft.whatActuallyHappened.trim() !== ""
    : draft.description.trim() !== "";

  async function submit() {
    const token = getAccessToken();
    if (!token || !canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isBug) {
        await api.submitBugReport(
          token,
          {
            whatWereYouDoing: draft.whatWereYouDoing.trim(),
            whatDidYouExpect: draft.whatDidYouExpect.trim(),
            whatActuallyHappened: draft.whatActuallyHappened.trim(),
            priority: draft.priority,
          },
          attachments.files,
        );
      } else {
        await api.submitFeatureRequest(token, { description: draft.description.trim(), priority: draft.priority }, attachments.files);
      }
      attachments.reset();
      setSent(true);
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong sending your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 99,
            background: hexA("#3eb034", 0.14),
            color: "#3eb034",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 14px",
          }}
        >
          <Icon name="check" size={26} />
        </div>
        <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--text)" }}>Thanks for the feedback</div>
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 15 }}>Your note has reached the team. We read every one.</p>
        <button
          onClick={() => { setSent(false); onCancel(); }}
          style={{ ...secondaryBtnStyle, marginTop: 18 }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    // Paste is caught on the document while this panel is open, so a screenshot lands as an attachment
    // wherever the caret is; the ref marks the panel out from the rest of the app (KEY-500).
    <div ref={attachments.surfaceRef} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8 }}>
        {([
          { key: "Bug", label: "Report a bug" },
          { key: "Feature", label: "Suggest a feature" },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => onChange({ tab: t.key })}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              font: "inherit",
              fontSize: 13.5,
              fontWeight: 600,
              border: `var(--border-w) solid ${draft.tab === t.key ? "var(--accent-border)" : "var(--border)"}`,
              background: draft.tab === t.key ? "var(--accent-soft)" : "var(--surface-2)",
              color: draft.tab === t.key ? "var(--accent-text)" : "var(--text-muted)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isBug ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={feedbackLabelStyle}>What were you doing?</label>
            <textarea value={draft.whatWereYouDoing} onChange={(e) => onChange({ whatWereYouDoing: e.target.value })} rows={2} placeholder="The steps you took…" style={feedbackFieldStyle} />
          </div>
          <div>
            <label style={feedbackLabelStyle}>What did you expect to happen?</label>
            <textarea value={draft.whatDidYouExpect} onChange={(e) => onChange({ whatDidYouExpect: e.target.value })} rows={2} placeholder="What you thought would happen…" style={feedbackFieldStyle} />
          </div>
          <div>
            <label style={feedbackLabelStyle}>What actually happened?</label>
            <textarea value={draft.whatActuallyHappened} onChange={(e) => onChange({ whatActuallyHappened: e.target.value })} rows={2} placeholder="What went wrong…" style={feedbackFieldStyle} />
          </div>
        </div>
      ) : (
        <div>
          <label style={feedbackLabelStyle}>What would you like to see?</label>
          <textarea value={draft.description} onChange={(e) => onChange({ description: e.target.value })} rows={5} placeholder="Describe the feature or improvement…" style={feedbackFieldStyle} />
        </div>
      )}

      <div>
        <label style={feedbackLabelStyle}>Priority</label>
        <div style={{ display: "flex", gap: 8 }}>
          {PRIORITIES.map((p) => (
            <button
              key={p.value}
              onClick={() => onChange({ priority: p.value })}
              title={p.hint}
              style={{
                flex: 1,
                padding: "8px 6px",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                border: `var(--border-w) solid ${draft.priority === p.value ? "var(--accent-border)" : "var(--border)"}`,
                background: draft.priority === p.value ? "var(--accent-soft)" : "var(--surface-2)",
                color: draft.priority === p.value ? "var(--accent-text)" : "var(--text-muted)",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <AttachmentTray state={attachments} />
      {/* Attachments are File objects and cannot be serialised, so unlike the text they do NOT
          survive a reload. They do survive collapsing and navigating, because the dock never
          unmounts. Said plainly rather than implied, so nobody reloads expecting a screenshot back. */}
      {attachments.files.length > 0 && (
        <p style={{ margin: 0, color: "var(--text-faint)", fontSize: 11.5 }}>
          Your text is saved on this device, but attachments aren't — send before reloading.
        </p>
      )}

      {error && <p style={{ margin: 0, color: "var(--danger, #d64545)", fontSize: 13.5 }}>{error}</p>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={secondaryBtnStyle}>{cancelLabel}</button>
        <button
          onClick={() => void submit()}
          disabled={!canSubmit || submitting}
          className="kd-btn-primary"
          style={{ opacity: canSubmit && !submitting ? 1 : 0.5 }}
        >
          {submitting ? "Sending…" : isBug ? "Send bug report" : "Send feature request"}
        </button>
      </div>
    </div>
  );
}
